import React, { useState } from "react";
import { api, formatErr } from "../api";
import {
  Sparkle, X, ChartBar, ShieldCheck, Users, Star, Target,
  CheckCircle, Warning, Info, Lightning,
} from "@phosphor-icons/react";
import { Avatar, ProgressBar } from "./Atoms";
import { toast } from "sonner";

/** Reusable trigger button — drop this in next to a creator row/card. */
export const AIAuditButton = ({ ambassadorId, projectId, label = "AI Audit", size = "sm", onClick, testIdSuffix }) => {
  const cls = size === "sm"
    ? "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#8B5CF6]/15 to-[#EC4899]/15 border border-[#8B5CF6]/30 text-[#EC4899] hover:from-[#8B5CF6]/25 hover:to-[#EC4899]/25 hover:text-white text-xs font-bold transition"
    : "inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white font-bold text-sm hover:opacity-90 transition glow-purple";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`ai-audit-btn-${testIdSuffix || ambassadorId}`}
      data-ambassador={ambassadorId}
      data-project={projectId || ""}
      className={cls}
    >
      <Sparkle size={size === "sm" ? 12 : 14} weight="fill" /> {label}
    </button>
  );
};

/** Modal panel — controlled. Pass { ambassadorId, projectId, onClose }. */
export const AIAuditPanel = ({ ambassadorId, projectId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    if (!ambassadorId) return;
    setLoading(true);
    const qs = projectId ? `?project_id=${projectId}` : "";
    api.get(`/ai/audit/${ambassadorId}${qs}`)
      .then(r => setData(r.data))
      .catch(e => { toast.error(formatErr(e)); onClose?.(); })
      .finally(() => setLoading(false));
  }, [ambassadorId, projectId, onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="ai-audit-modal"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative glass max-w-3xl w-full p-5 sm:p-7 my-auto"
        onClick={e => e.stopPropagation()}
        style={{ background: "linear-gradient(180deg, rgba(139,92,246,0.08), rgba(15,15,20,0.85))" }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center glow-purple">
              <Sparkle size={18} weight="fill" color="white" />
            </div>
            <div>
              <div className="text-white font-extrabold text-lg leading-tight">AI Creator Audit</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="ai-audit-close"
            className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 grid place-items-center text-white">
            <X size={16} weight="bold" />
          </button>
        </div>

        {loading || !data ? (
          <div className="space-y-3 py-6">
            <div className="h-20 rounded-2xl bg-white/5 animate-pulse" />
            <div className="grid grid-cols-3 gap-3">
              <div className="h-20 rounded-2xl bg-white/5 animate-pulse" />
              <div className="h-20 rounded-2xl bg-white/5 animate-pulse" />
              <div className="h-20 rounded-2xl bg-white/5 animate-pulse" />
            </div>
            <div className="h-32 rounded-2xl bg-white/5 animate-pulse" />
          </div>
        ) : (
          <AuditBody data={data} />
        )}
      </div>
    </div>
  );
};

const AuditBody = ({ data }) => {
  const a = data.ambassador || {};
  const ctx = data.project_context;
  return (
    <div className="space-y-4">
      {/* Creator row + Fit score hero */}
      <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10">
        <Avatar src={a.photo} name={a.name} size={52} />
        <div className="min-w-0 flex-1">
          <div className="text-white font-bold text-base truncate" data-testid="audit-creator-name">{a.name || "—"}</div>
          <div className="text-xs text-muted truncate">
            {[a.city, a.gender, a.college].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted">Creator Fit</div>
          <div className="text-3xl font-black" data-testid="audit-fit-score" style={{ color: scoreColor(data.creator_fit_score) }}>
            {data.creator_fit_score}<span className="text-base text-muted">/100</span>
          </div>
        </div>
      </div>

      {/* 3 sub-scores */}
      <div className="grid grid-cols-3 gap-3">
        <SubScore icon={Users}       label="Profile Strength"  value={data.profile_strength}  testid="audit-profile-strength" />
        <SubScore icon={ChartBar}    label="Platform Strength" value={data.platform_strength} testid="audit-platform-strength" />
        <SubScore icon={ShieldCheck} label="Brand Safety"      value={data.brand_safety}      testid="audit-brand-safety" />
      </div>

      {/* Project relevance block (only if audit was opened from a project) */}
      {ctx && (
        <div className="p-4 rounded-2xl bg-gradient-to-br from-[#22D3EE]/8 to-[#8B5CF6]/8 border border-[#22D3EE]/30" data-testid="audit-project-context">
          <div className="flex items-center gap-2 mb-2">
            <Target size={16} weight="bold" className="text-[#22D3EE]" />
            <div className="text-white font-bold">Campaign relevance</div>
            <div className="ml-auto text-[10px] uppercase tracking-wider font-bold text-muted">
              {ctx.project_title} · {ctx.category}
            </div>
          </div>
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-muted font-bold">Keyword Match Score</span>
              <span className="text-white font-bold" data-testid="audit-keyword-score">{ctx.keyword_match_score}/100</span>
            </div>
            <ProgressBar value={ctx.keyword_match_score} max={100} color="#22D3EE" />
          </div>
          {ctx.matched_keywords?.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-wider text-muted font-bold mb-1">Matched Keywords</div>
              <div className="flex flex-wrap gap-1.5" data-testid="audit-matched-keywords">
                {ctx.matched_keywords.map(k => (
                  <span key={k} className="px-2 py-0.5 rounded-full text-[11px] font-bold border border-[#22D3EE]/40 text-[#22D3EE] bg-[#22D3EE]/10">
                    {k}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="text-sm text-white/90">{ctx.project_relevance}</div>
        </div>
      )}

      {/* Recommendation banner */}
      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#A3E635]/10 to-[#10B981]/5 border border-[#A3E635]/30 flex items-start gap-3" data-testid="audit-recommendation">
        <Lightning size={18} weight="fill" className="text-[#A3E635] mt-0.5" />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted font-bold mb-0.5">Recommendation</div>
          <div className="text-white text-sm font-semibold">{data.recommendation}</div>
        </div>
      </div>

      {/* Two-column: strengths + best fit, then missing + risks */}
      <div className="grid sm:grid-cols-2 gap-3">
        <BulletList icon={Star}        title="Strengths"            color="#A3E635" items={data.strengths}           testid="audit-strengths" />
        <BulletList icon={Target}      title="Best fit campaigns"   color="#22D3EE" items={data.best_fit_campaigns}  testid="audit-best-fit" />
        <BulletList icon={Info}        title="Missing information"  color="#F59E0B" items={data.missing_information} testid="audit-missing"
                    empty="Profile is fully filled out." />
        <BulletList icon={Warning}     title="Risks / Concerns"     color="#EC4899" items={data.risks}               testid="audit-risks"
                    empty="No notable risks detected." />
      </div>

      {/* Tiny stats footer */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="audit-stats">
        <Stat label="IG Followers"  value={fmt(data.stats.instagram_followers)} />
        <Stat label="Approved Subs" value={data.stats.approved_submissions}   ok />
        <Stat label="Pending Subs"  value={data.stats.pending_submissions} />
        <Stat label="Rejected"      value={data.stats.rejected_submissions}    bad />
      </div>
    </div>
  );
};

const SubScore = ({ icon: Icon, label, value, testid }) => (
  <div className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted font-bold">
      <Icon size={12} weight="bold" /> {label}
    </div>
    <div className="text-xl font-extrabold mt-0.5" style={{ color: scoreColor(value) }}>{value}<span className="text-xs text-muted">/100</span></div>
    <div className="mt-1.5"><ProgressBar value={value} max={100} color={scoreColor(value)} /></div>
  </div>
);

const BulletList = ({ icon: Icon, title, color, items = [], testid, empty }) => (
  <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} weight="bold" style={{ color }} />
      <div className="text-white font-bold text-sm">{title}</div>
    </div>
    {items.length === 0 ? (
      <div className="text-xs text-muted italic flex items-center gap-1.5">
        <CheckCircle size={12} weight="fill" className="text-[#A3E635]" /> {empty || "—"}
      </div>
    ) : (
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-white/85 leading-snug flex items-start gap-2">
            <span className="w-1 h-1 mt-2 rounded-full flex-shrink-0" style={{ background: color }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Stat = ({ label, value, ok, bad }) => (
  <div className="p-2 rounded-xl bg-white/3 border border-white/5">
    <div className="text-[10px] uppercase tracking-wider text-muted font-bold">{label}</div>
    <div className={`text-lg font-bold ${ok ? "text-[#A3E635]" : bad ? "text-[#EC4899]" : "text-white"}`}>{value}</div>
  </div>
);

const fmt = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000) return (v / 1000).toFixed(v >= 10000 ? 0 : 1) + "k";
  return String(v);
};
const scoreColor = (s) => s >= 75 ? "#A3E635" : s >= 55 ? "#22D3EE" : s >= 35 ? "#F59E0B" : "#EC4899";

/**
 * Convenience hook for pages that already have a "selected creator" pattern.
 * Returns: { audit, openAudit, closeAudit, AuditModal }
 *
 *   const { openAudit, AuditModal } = useAIAudit();
 *   ...
 *   <Button onClick={() => openAudit(ambId, projectId)}>AI Audit</Button>
 *   {AuditModal}
 */
export const useAIAudit = () => {
  const [target, setTarget] = useState(null); // { ambassadorId, projectId } | null
  const openAudit  = (ambassadorId, projectId = null) => setTarget({ ambassadorId, projectId });
  const closeAudit = () => setTarget(null);
  const AuditModal = target
    ? <AIAuditPanel ambassadorId={target.ambassadorId} projectId={target.projectId} onClose={closeAudit} />
    : null;
  return { target, openAudit, closeAudit, AuditModal };
};
